import { EXPRESS_DELIVERY_FILTER } from '@common/constants/features';

import {
  COLOR_FILTER_ID,
  DELIVERY_FILTER_ID,
  DELIVERY_WO_EXP_FILTER_ID,
  EXP_DELIVERY_FILTER_ID,
  PICKUP_FILTER_ID,
  PRICE_FILTER_ID,
  SIZE_FILTER_ID,
  SORT_PARAM_ID,
} from '../consts';

const filterByIdGetter = (filterId) => (state) => state.filters.find((filter) => filter.id === filterId);

const appliedFilterValuesGetter = (filterId) => (_, getters) => getters.appliedFilters
  .find((filter) => filter.id === filterId)
  ?.values ?? [];

export default {
  namespaced: true,

  state: () => ({
    categoryName: '',
    categoryId: '',
    categoriesTree: {},
    breadcrumbs: [],
    subqueryRef: '',
    subqueryRefWoFacets: '',
    productsCount: 0,
    products: [],
    filters: [],
    sort: '',
    loadProgress: false,
    totalPages: 0,
    page: 1,
    pageSize: 36,
    tags: [],
  }),

  getters: {
    products: (state) => state.products,
    filters: (state) => state.filters,
    productsCount: (state) => state.productsCount,
    loadProgress: (state) => state.loadProgress,
    end: (state) => state.page >= state.totalPages,
    sort: (state) => state.sort,
    page: (state) => state.page,
    pageSize: (state) => state.pageSize,
    totalPages: (state) => state.totalPages,
    breadcrumbs: (state) => state.breadcrumbs,

    categoryName: (state) => state.categoryName,
    categoryUrl: (state) => state.categoryPath || '',
    categoryId: (state) => state.categoryId,
    categoriesTree: (state) => state.categoriesTree,

    subqueryRef: (state) => state.subqueryRef ?? '',
    subqueryRefPath: (state) => state.subqueryRef.split('?')?.[0] ?? '',
    subqueryRefParams: (state, getters) => {
      const params = new URLSearchParams(state.subqueryRef.split('?')?.[1] ?? '');
      params.set('page', getters.page);
      return params;
    },
    subqueryRefWoFacets: (state) => state.subqueryRefWoFacets,

    pageParams: (state, getters) => {
      const params = new URLSearchParams(state.subqueryRef.split('?')?.[1] ?? '');
      params.set('page', getters.page);

      return params;
    },

    sizeFilter: filterByIdGetter(SIZE_FILTER_ID),
    colorFilter: filterByIdGetter(COLOR_FILTER_ID),
    priceFilter: filterByIdGetter(PRICE_FILTER_ID),
    pickupFilter: filterByIdGetter(PICKUP_FILTER_ID),

    sizeFilterAppliedValues: appliedFilterValuesGetter(SIZE_FILTER_ID),
    colorFilterAppliedValues: appliedFilterValuesGetter(COLOR_FILTER_ID),
    priceFilterAppliedValues: appliedFilterValuesGetter(PRICE_FILTER_ID),
    pickupFilterAppliedValues: appliedFilterValuesGetter(PICKUP_FILTER_ID),

    appliedFilters: (state, getters, rootState) => {
      const hasExpressDeliveryFT = rootState.features.$features[EXPRESS_DELIVERY_FILTER];

      let selectedItems = state.filters.filter((it) => it.selected);

      const hasDelivery = !!selectedItems.find((item) => item.id === DELIVERY_FILTER_ID);
      const hasDeliveryWithoutExpress = !!selectedItems.find((item) => item.id === DELIVERY_WO_EXP_FILTER_ID);

      if (hasDelivery && hasDeliveryWithoutExpress) {
        // Так как delivery=delivery_wo_exp+exp_delivery
        // то что бы не было лишнего числа в каунтере кнопки, удаляем не нужный фильтр
        selectedItems = selectedItems.filter((item) => {
          if (item.id === DELIVERY_FILTER_ID) {
            return false;
          }

          return true;
        });
      }

      if (!hasExpressDeliveryFT) {
        // TODO: удалить код после удаления FT: EXPRESS_DELIVERY_FILTER
        selectedItems = selectedItems.filter((item) => {
          if (item.id === EXP_DELIVERY_FILTER_ID) {
            return false;
          }

          return true;
        });
      }

      return selectedItems.map(
        (filter) => ({
          ...filter,
          values: filter.values.filter((it) => it.selected),
        }),
      );
    },

    getFilter: (state) => (filterId) => state.filters.find((filter) => filter.id === filterId),
  },

  actions: {

    async loadCatalogData({ getters, commit }, { categoryUrl, page = 1 }) {
      if (getters.loadProgress || (categoryUrl === getters.subqueryRef && page === getters.page)) {
        return;
      }

      commit('setLoadProgress');
      try {
        const catalogData = await this.$apiBFF.fetchCatalog({
          url: categoryUrl,
          page,
          isFetchBreadcrumbs: true,
        });

        commit('setCatalogData', catalogData);
      } finally {
        commit('setLoadProgress', false);
      }
    },

    async loadCatalogDataMore({ getters, commit }) {
      commit('setLoadProgress');

      const catalogData = await this.$apiBFF.fetchCatalog({
        url: getters.subqueryRef,
        page: getters.page + 1,
        isFetchBreadcrumbs: false,
      });

      commit('appendProducts', catalogData);
      commit('setLoadProgress', false);
    },

    async loadCategoriesTree({ commit }, categoryUrl) {
      const result = await this.$apiBFF.fetchCatalogCategoriesTree(categoryUrl);

      commit('setCategoriesTree', result);
    },

    /**
     * В текущем дизайне фильтров невозможно работать просто переходом по ссылкам, поэтому сохранена старая логика
     */
    applyCategoryFilter({ dispatch, getters }, { id, value } = {}) {
      if (!id || !value) {
        return;
      }
      const origFilter = getters.getFilter(id);
      const params = getters.subqueryRefParams;

      switch (id) {
        case PRICE_FILTER_ID: {
          const fromKey = `f-${origFilter.codeUrl}from`;
          const toKey = `f-${origFilter.codeUrl}to`;
          const { from, to } = value?.[0] ?? {};
          const { min, max } = origFilter?.values?.[0] ?? {};

          if (from === min && to === max) {
            params.delete(fromKey);
            params.delete(toKey);
          } else if (from && to && max && min) {
            params.set(fromKey, Math.max(from, min));
            params.set(toKey, Math.min(to, max));
          }
          break;
        }
        default: {
          const key = `f-${origFilter.codeUrl}`;
          const values = value.map((filterValue) => filterValue.value);

          if (values.length) {
            params.set(key, values);
          } else {
            params.delete(key);
          }
        }
      }

      const paramsString = params.toString();
      const url = paramsString.length ? `${getters.subqueryRefPath}?${paramsString}` : getters.subqueryRefPath;

      return dispatch('loadCatalogData', { categoryUrl: url });
    },

    applySort({ getters, dispatch }, value) {
      const params = getters.subqueryRefParams;

      if (params.has(SORT_PARAM_ID)) {
        params.delete(SORT_PARAM_ID);
      }
      if (params.has('page')) {
        params.delete('page');
      }
      params.set(SORT_PARAM_ID, value);

      const url = `${getters.subqueryRefPath}?${params.toString()}`;
      return dispatch('loadCatalogData', { categoryUrl: url });
    },

    clearAllFilters({ dispatch, state }) {
      dispatch('loadCatalogData', state.subqueryRefWoFacets);
    },
  },

  mutations: {
    setCatalogData: (state, {
      id,
      name,
      path,
      sort,
      products,
      productsCount,
      pagination,
      filters,
      subqueryRef,
      subqueryRefWoFacets,
      breadcrumbs,
      tags,
    }) => {
      state.categoryId = id;
      state.categoryName = name;
      state.categoryPath = path;
      state.sort = sort;
      state.products = products;
      state.productsCount = productsCount;
      state.page = pagination.page;
      state.totalPages = pagination.pagesCount;
      state.pageSize = pagination.pageSize;
      state.filters = filters;
      state.subqueryRef = subqueryRef;
      state.subqueryRefWoFacets = subqueryRefWoFacets;
      state.breadcrumbs = breadcrumbs;
      state.tags = tags;
    },

    setCategoriesTree(state, categoriesTree) {
      state.categoriesTree = categoriesTree;
    },

    appendProducts: (state, {
      products,
      productsCount,
      pagination,
    }) => {
      products.forEach((product) => state.products.push(product));
      state.productsCount = productsCount;
      state.page = pagination.page;
    },

    setLoadProgress: (state, loadProgress = true) => {
      state.loadProgress = loadProgress;
    },
  },
};
